import { useCallback, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useUsers } from '../../../contexts/users/UsersContext';
import { buildSignedMessage } from '../../../utils/messageSignature';

interface UseChatSignatureReturn {
    /** Nome que será usado na assinatura */
    signatureName: string;
    /** Adiciona a assinatura ao texto */
    applySignature: (text: string) => string;
}

export const useChatSignature = (): UseChatSignatureReturn => {
    const { user } = useAuth();
    const { crmUsers } = useUsers();

    // Obtém o nome do usuário atual para a assinatura
    const signatureName = useMemo(() => {
        // Prioridade: user_metadata.full_name > user_metadata.name > CRM User > 'Atendente'
        if (user?.user_metadata?.full_name) {
            return user.user_metadata.full_name;
        }

        if (user?.user_metadata?.name) {
            return user.user_metadata.name;
        }

        const currentCrmUser = crmUsers.find(u => u.id === user?.id);
        if (currentCrmUser?.name && currentCrmUser.name !== 'Usuário') {
            return currentCrmUser.name;
        }

        return 'Atendente';
    }, [user, crmUsers]);

    /**
     * Adiciona a assinatura no início do texto.
     * Formato: nome do atendente em negrito + quebra de linha + texto original
     * O WhatsApp usa *texto* para negrito.
     */
    const applySignature = useCallback((text: string): string => {
        if (!signatureName) return text;
        return buildSignedMessage(signatureName, text);
    }, [signatureName]);

    return {
        signatureName,
        applySignature
    };
};
